import { useState, useEffect, useContext } from "react"
import { GlobalContextProvider } from "../components/functions/GlobalContext"
import {
    Box,
    Text,
    Center,
    HStack,
    Button,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    Spinner,
    Icon,
} from "@chakra-ui/react"
import { AiOutlineCheckCircle, AiOutlineCloseCircle } from 'react-icons/ai'
import { ChevronLeftIcon } from '@chakra-ui/icons'
import InfoModal from "../components/modals/InfoModal"
import Const from "../Consts"
import { Twirp, TwirpProps } from "../components/functions/Twirp"
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

type PesilatRow = {
    id: number
    nama: string
    username: string
    country_id: number
    tahun_masuk: number
    athlete: boolean
    coach_level: number
    referee_level: number
}

const LEVEL_LIST = [
    { value: 0, label: "-" },
    { value: 1, label: "Ranting" },
    { value: 2, label: "City 1" }, { value: 3, label: "City 2" }, { value: 4, label: "City 3" },
    { value: 5, label: "Province 1" }, { value: 6, label: "Province 2" }, { value: 7, label: "Province 3" },
    { value: 8, label: "National 1" }, { value: 9, label: "National 2" }, { value: 10, label: "National 3" },
    { value: 11, label: "International 1" }, { value: 12, label: "International 2" }, { value: 13, label: "International 3" },
]

export default function RantingPesilatPage() {
    const globalContext = useContext(GlobalContextProvider)
    const { t } = useTranslation();
    const navigate = useNavigate()
    const location = useLocation()
    const { rantingid } = useParams()

    const [rantingName, setRantingName] = useState((location.state as any)?.rantingName || "")

    const [rows, setRows] = useState<PesilatRow[]>([])
    const [loading, setLoading] = useState(false)
    const [countryMap, setCountryMap] = useState<Record<number, string>>({})

    const [modalData, setModalData] = useState({
        title: "",
        main: "",
        isOpen: false,
    })
    const closeModal = () => {
        setModalData({ ...modalData, isOpen: false })
    }

    const findLevelLabel = (value: number) => {
        const found = LEVEL_LIST.find(item => item.value === value)
        return found ? found.label : "-"
    }

    const loadCountries = async () => {
        const props: TwirpProps = {
            url: Const.API_ListCountries, headers: {}, timeout: 5000, data: {}
        }
        const result = await Twirp(props)
        if (result.state == 0) {
            const commons = result.data.Commons as { id: number, nama: string }[]
            const map: Record<number, string> = {}
            commons.forEach(c => { map[c.id] = c.nama })
            setCountryMap(map)
        }
    }

    const loadRantingName = async () => {
        const props: TwirpProps = {
            url: Const.API_ReadRanting,
            headers: { Authorization: globalContext?.GetLoginData().token },
            timeout: 10000,
            data: { id: +(rantingid || "0") }
        }
        const result = await Twirp(props)
        if (result.state == 0) {
            setRantingName(result.data.nama)
        }
    }

    const loadData = async () => {
        setLoading(true)
        const props: TwirpProps = {
            url: Const.API_ListPesilatRanting,
            headers: { Authorization: globalContext?.GetLoginData().token },
            timeout: 10000,
            data: { id: + (rantingid || "0") }
        }
        const result = await Twirp(props)
        if (result.state == 2) {//other error
            setModalData({ ...modalData, isOpen: true, title: result.axiosError.code, main: result.axiosError.message })
        } else if (result.state == 1) {//twirp controlled error
            let msg = result.axiosError.response?.data?.msg || result.axiosError.message
            setModalData({ ...modalData, isOpen: true, title: result.axiosError.code, main: msg })
            if (result.axiosError.response?.status == 401) {
                navigate('/logout')
            }
        } else { //successful
            setRows(result.data.Pesilats || [])
        }
        setLoading(false)
    }

    useEffect(() => {
        let username = globalContext?.GetLoginData().username || ""
        if (username === "") {
            navigate('/login')
            return
        }
        if (rantingName === "") {
            loadRantingName()
        }
        loadCountries()
        loadData()
    }, [rantingid])

    const onNameClick = (pesilatId: number) => {
        navigate("/profileview/" + pesilatId)
    }

    return (
        <Box py={"10px"} px={"10px"} borderWidth='0px' borderRadius='lg' overflow='hidden'>
            <InfoModal isOpen={modalData.isOpen} title={modalData.title} mainText={modalData.main} closeFunction={closeModal} />

            <HStack spacing={2} mb={"10px"}>
                <Button size='sm' leftIcon={<ChevronLeftIcon />} variant='outline' onClick={() => navigate('/reports')}>
                    {t('Back')}
                </Button>
                <Text fontSize='md' fontWeight={'bold'}>
                    {t('Ranting')}{rantingName !== "" ? ": " + rantingName : ""}
                </Text>
            </HStack>

            {loading ? (
                <Center py={"20px"}>
                    <Spinner size='lg' />
                </Center>
            ) : (
                <TableContainer>
                    <Table variant='simple' colorScheme='blue' size='sm'>
                        <Thead>
                            <Tr>
                                <Th>#</Th>
                                <Th>{t('Full Name')}</Th>
                                <Th>{t('Username')}</Th>
                                <Th>{t('Country')}</Th>
                                <Th>{t('joined_kpsn')}</Th>
                                <Th>{t('Athlete')}</Th>
                                <Th>{t('Coach Level')}</Th>
                                <Th>{t('Referee Level')}</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {rows.length === 0 ? (
                                <Tr>
                                    <Td colSpan={8}>
                                        <Center>{t('no_records')}</Center>
                                    </Td>
                                </Tr>
                            ) : (
                                rows.map((row, index) => (
                                    <Tr key={row.id}>
                                        <Td>{index + 1}</Td>
                                        <Td>
                                            <Button variant='link' colorScheme='blue' size='sm' onClick={() => onNameClick(row.id)}>
                                                {row.nama}
                                            </Button>
                                        </Td>
                                        <Td>{row.username}</Td>
                                        <Td>{countryMap[row.country_id] || row.country_id}</Td>
                                        <Td>{row.tahun_masuk}</Td>
                                        <Td>
                                            {row.athlete ? (
                                                <Icon as={AiOutlineCheckCircle} color='green.400' w={5} h={5} />
                                            ) : (
                                                <Icon as={AiOutlineCloseCircle} color='gray.400' w={5} h={5} />
                                            )}
                                        </Td>
                                        <Td>{row.coach_level > 0 ? t(findLevelLabel(row.coach_level)) : '-'}</Td>
                                        <Td>{row.referee_level > 0 ? t(findLevelLabel(row.referee_level)) : '-'}</Td>
                                    </Tr>
                                ))
                            )}
                        </Tbody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    )
}
